/** Same-origin client for the server-proxied Foursquare Place Search API. */

import type { LngLat, Place } from '../core/types';
import { distance } from '../core/geo';
import { getLang } from '../core/i18n';

const ENDPOINT = `${import.meta.env.BASE_URL}api/foursquare/places/search`;
const LS_SESSION = 'wayfarer.session';

interface FoursquarePlaceWire {
  id: string;
  providerPlaceId: string;
  name: string;
  address: string;
  center: [number, number];
  primaryType?: string;
  provider: 'foursquare';
}

export async function searchFoursquarePlaces(
  query: string,
  origin: LngLat | null,
  signal?: AbortSignal,
): Promise<Place[]> {
  let token: string | null = null;
  try {
    token = localStorage.getItem(LS_SESSION);
  } catch {
    /* cookie auth remains available */
  }

  const res = await fetch(ENDPOINT, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'X-Wf-Session': token } : {}),
    },
    body: JSON.stringify({
      q: query,
      ...(origin ? { lat: origin[1], lng: origin[0] } : {}),
      language: getLang(),
      maxResults: 8,
    }),
    signal: signal ?? AbortSignal.timeout(9_000),
  });

  if (!res.ok) throw new Error(`Foursquare Places search failed (${res.status})`);
  const body = (await res.json()) as { places?: FoursquarePlaceWire[] };
  return (body.places ?? [])
    .map((place): Place | null => {
      const center = place.center;
      if (
        !Array.isArray(center) ||
        center.length !== 2 ||
        !Number.isFinite(center[0]) ||
        !Number.isFinite(center[1])
      ) {
        return null;
      }
      return {
        id: place.id || `foursquare:${place.providerPlaceId}`,
        providerPlaceId: place.providerPlaceId || place.id,
        provider: 'foursquare',
        name: place.name,
        address: place.address ?? '',
        center: [center[0], center[1]],
        kind: place.primaryType || 'poi',
        distance: origin ? distance(origin, center) : undefined,
      };
    })
    .filter((place): place is Place => place !== null);
}
